/**
 * Mail-In Log — Malaysia team dashboard.
 *
 * Priority-ordered queue of every open claim, driven by the customer's tier
 * (Vault > Household > Hunter > Seeker > Finder > non-member). Country
 * filter switches between UK and Malaysia decks; the entire page uses the
 * new 7-stage pipeline from the Master Ops Map.
 *
 * Available at: /app/crm/mail-in-log (linked from CRM shell)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, AlertTriangle, Package, Clock, RefreshCw, Globe, Filter, Printer, Camera, PackageOpen } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const TIER_COLORS = {
  vault: "bg-gold/20 text-yellow-700",
  household: "bg-primary/20 text-primary",
  hunter: "bg-slate-300/40 text-slate-700",
  seeker: "bg-bronze/20 text-orange-700",
  finder: "bg-muted text-muted-foreground",
  none: "bg-muted text-muted-foreground",
};

export default function MailInLog() {
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [filter, setFilter] = useState({ country_id: "UK", stage: "all", stuck: "all" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { country_id: filter.country_id };
      if (filter.stage !== "all") params.stage = filter.stage;
      if (filter.stuck === "true") params.stuck = true;
      const [q, s] = await Promise.all([
        api.get("/mail-in-log", { params }),
        api.get("/mail-in-log/stages"),
      ]);
      setRows(q.data || []);
      setStages(s.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); const iv = setInterval(load, 30_000); return () => clearInterval(iv); }, [load]);

  const stuckCount = useMemo(() => rows.filter((r) => r.stuck_flag).length, [rows]);
  const byStage = useMemo(() => {
    const acc = {};
    for (const r of rows) acc[r.mail_in_stage] = (acc[r.mail_in_stage] || 0) + 1;
    return acc;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="mail-in-log-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline text-secondary">Mail-In Log</div>
          <h2 className="mt-1 font-display text-3xl font-light tracking-tight">
            {filter.country_id === "MY" ? "🇲🇾 Malaysia" : "🇬🇧 UK"} workshop queue
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Priority: <strong>Vault</strong> → Household → Hunter → Seeker → Finder → non-members. Auto-refreshes every 30s.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filter.country_id} onValueChange={(v) => setFilter((f) => ({ ...f, country_id: v }))}>
            <SelectTrigger className="w-32" data-testid="mailin-country"><Globe className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="UK">United Kingdom</SelectItem>
              <SelectItem value="MY">Malaysia</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.stage} onValueChange={(v) => setFilter((f) => ({ ...f, stage: v }))}>
            <SelectTrigger className="w-52" data-testid="mailin-stage"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {stages.map((s) => (
                <SelectItem key={s.code} value={s.code}>{s.label} ({byStage[s.code] || 0})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.stuck} onValueChange={(v) => setFilter((f) => ({ ...f, stuck: v }))}>
            <SelectTrigger className="w-36" data-testid="mailin-stuck">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All jobs</SelectItem>
              <SelectItem value="true">Stuck &gt; 48h ({stuckCount})</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="mailin-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stage strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {stages.map((s) => {
          const count = byStage[s.code] || 0;
          return (
            <button
              key={s.code}
              onClick={() => setFilter((f) => ({ ...f, stage: s.code === filter.stage ? "all" : s.code }))}
              className={`rounded-xl border p-3 text-left transition-colors ${
                filter.stage === s.code
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40"
              }`}
              data-testid={`mailin-stage-${s.code}`}
            >
              <div className="text-[10px] overline text-muted-foreground">{s.label}</div>
              <div className="mt-1 font-display text-2xl">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Queue table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border bg-muted/40 text-[11px] overline">
          <div className="col-span-2">Job #</div>
          <div className="col-span-3">Customer & device</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-2">Stage</div>
          <div className="col-span-2">Priority · Age</div>
          <div className="col-span-1"></div>
        </div>
        {rows.length === 0 && !loading && (
          <div className="p-10 text-center text-sm text-muted-foreground">Queue empty. 🎉</div>
        )}
        {rows.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelected(c)}
            className="w-full text-left grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center hover:bg-muted/30 transition-colors"
            data-testid={`mailin-row-${c.job_number}`}
          >
            <div className="col-span-2 font-mono text-sm">
              {c.job_number || c.case_number}
              {c.stuck_flag && <span className="ml-1 text-destructive" title="Stuck > 48h"><AlertTriangle className="w-3 h-3 inline" /></span>}
            </div>
            <div className="col-span-3 min-w-0">
              <div className="text-sm font-medium truncate">{c.description?.slice(0, 60) || "—"}</div>
              <div className="text-xs text-muted-foreground truncate">{c.issue_category}</div>
            </div>
            <div className="col-span-2">
              <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${TIER_COLORS[c.tier] || TIER_COLORS.none}`}>
                {c.tier || "none"}
              </span>
            </div>
            <div className="col-span-2 text-xs capitalize">{(c.mail_in_stage || "").replace(/_/g, " ")}</div>
            <div className="col-span-2 text-xs">
              <span className="font-mono">P{c.priority_rank}</span>
              <span className="text-muted-foreground ml-2 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {timeAgo(c.updated_at)}
              </span>
            </div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-muted-foreground" /></div>
          </button>
        ))}
      </div>

      <ClaimDrawer claim={selected} stages={stages} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}

function ClaimDrawer({ claim, stages, onClose, onChanged }) {
  const [nextStage, setNextStage] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!claim) return;
    const codes = stages.map((s) => s.code);
    const idx = codes.indexOf(claim.mail_in_stage);
    setNextStage(codes[Math.min(idx + 1, codes.length - 1)] || "");
    setNote("");
  }, [claim, stages]);

  if (!claim) return null;

  const advance = async () => {
    setSaving(true);
    try {
      await api.post(`/mail-in-log/${claim.id}/advance`, { stage: nextStage, note });
      toast.success(`Moved to ${nextStage.replace(/_/g, " ")}`);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const createLabel = async () => {
    try {
      const { data } = await api.post("/integrations/labels/create", {
        claim_id: claim.id, carrier: "royal_mail", service: "tracked_48",
      });
      toast.success(`Label created — tracking ${data.tracking_number}`);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      window.open(backend + data.label_url, "_blank");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const uploadIntakePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/mail-in-log/${claim.id}/intake-photo?stage=${claim.mail_in_stage}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Intake photo attached");
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  const assignLoaner = async () => {
    try {
      const { data } = await api.post(`/claims/${claim.id}/loaner/assign`);
      toast.success(`Loaner assigned: ${data.brand} ${data.model}`);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Loaner assign failed"); }
  };

  return (
    <Dialog open={!!claim} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mailin-drawer">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl">
            {claim.job_number || claim.case_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="p-3 rounded-md bg-muted/40">
            <div className="text-[10px] overline text-muted-foreground">Current stage</div>
            <div className="mt-1 font-medium capitalize">{(claim.mail_in_stage || "").replace(/_/g, " ")}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] overline text-muted-foreground">Tier</div>
              <div className="capitalize">{claim.tier}</div>
            </div>
            <div>
              <div className="text-[10px] overline text-muted-foreground">Priority</div>
              <div className="font-mono">P{claim.priority_rank}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] overline text-muted-foreground">Description</div>
            <div>{claim.description}</div>
          </div>
          <div>
            <div className="text-[10px] overline text-muted-foreground mb-1">Move to stage</div>
            <Select value={nextStage} onValueChange={setNextStage}>
              <SelectTrigger data-testid="mailin-drawer-nextstage"><SelectValue /></SelectTrigger>
              <SelectContent>
                {stages.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] overline text-muted-foreground mb-1">Note (optional, visible to customer)</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="mailin-drawer-note" />
          </div>
        </div>
        <DialogFooter>
          <div className="flex flex-wrap gap-2 justify-end w-full">
            <Button variant="outline" size="sm" onClick={createLabel} data-testid="mailin-label-btn">
              <Printer className="w-3.5 h-3.5 mr-1" /> Create label
            </Button>
            <label className="inline-flex" data-testid="mailin-intake-photo-label">
              <input type="file" accept="image/*" onChange={uploadIntakePhoto} className="hidden" />
              <Button variant="outline" size="sm" asChild data-testid="mailin-intake-photo-btn">
                <span className="cursor-pointer"><Camera className="w-3.5 h-3.5 mr-1" /> Intake photo</span>
              </Button>
            </label>
            {(claim.tier === "vault" || claim.tier === "household") && !claim.loaner_id && (
              <Button variant="outline" size="sm" onClick={assignLoaner} data-testid="mailin-loaner-btn">
                <PackageOpen className="w-3.5 h-3.5 mr-1" /> Assign loaner
              </Button>
            )}
            <Button onClick={advance} disabled={saving} data-testid="mailin-drawer-advance">
              {saving ? "Updating…" : "Advance stage"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
